import axios from 'axios';
import { FormConfig, FormFieldConfig } from '../components/DynamicForm';

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => {
    // Check for error codes in the response
    if (response.data && 
        typeof response.data === 'object' && 
        'code' in response.data && 
        response.data.code !== 0) {
      // You can handle specific error codes here if needed
      const errorMsg = 'msg' in response.data ? response.data.msg : 'Unknown error';
      console.warn(`API Error: ${errorMsg}`);
    }
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      // Display a message to the user about the authentication error
      console.error('Authentication error: Your session has expired or you are not authorized');
      
      // You can also use a message system if available in the global scope
      if (typeof window !== 'undefined' && window.console) {
        console.warn('您的登录已过期，请重新登录');
      }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

interface LoginResponse {
  code: number;
  data?: {
    access_token: string;
    uid: number;
    realName: string;
  };
  msg: string;
}

interface ApiResponse<T = any> {
  code: number;
  msg: string;
  data?: T;
}

interface ProjectListResponse {
  code: number;
  data?: Project[];
  msg: string;
}

export interface Project {
  id: number;
  name: string;
  title?: string;
  projectCode: string;
  addTime: string;
  area: number;
  region: string;
  appKey: string;
  appSecret: string;
  buildId: number | null;
  token: string | null;
  tokenExpire: number;
  uid: number;
  exData: Record<string, any> | null;
  status?: number; // 可能存在的状态字段
}

// Remove the exported Device interface and deviceTypeMap
interface DeviceType {
  id: number;
  deviceCode: string;
  deviceName: string;
  data: any; // JSON type
  isPay: number; // tinyint
  project: number; // Project ID
  addTime: string; // datetime
  deviceType: number; // smallint: 0培吊 1扬尘 3视频 4卸料平台 5施工升降机
  thirdType: string;
  area: number;
  beian: string;
  exData: {
    driverName?: string;
    idcardNumber?: string;
    monitorKey?: string;
    [key: string]: any;  // Allow for other properties
  };
  deviceFactory: string;
  latitude: string;
  longitude: string;
  uid: string | null;
  price: number | null;
  isDel: number; // tinyint: 0默认否，1删除了
  remark: string | null;
}

interface ExDataItem {
  type: string;
  is_ex: boolean | number;
  label: string;
  model: string;
  placeholder: string;
}

// API functions
export const loginApi = async (account: string, password: string): Promise<{ data: LoginResponse }> => {
  try {
    // Make a real API call to the login endpoint
    const response = await api.post<LoginResponse>('/member/login', {
      account,
      password
    });
    
    return response;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const submitDriverData = async (data: any): Promise<{ data: ApiResponse }> => {
  try {
    // In a real app, this would be a real API call
    // For demo, we'll simulate a successful response
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('Submitted data:', data);
        resolve({
          data: {
            code: 0,
            msg: '数据提交成功',
            data: null
          },
        });
      }, 1500);
    });
  } catch (error) {
    console.error('API error:', error);
    throw error;
  }
};

export const getMyProjects = async (): Promise<{ data: ProjectListResponse }> => {
  try {
    const response = await api.get<ProjectListResponse>('/areas/my');
    return response;
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }
};

// New API function to get projects by protocol ID
export const getProjectsByProtocolId = async (protocolId: number): Promise<{ data: { code: number, msg: string, data?: Project[] } }> => {
  try {
    const response = await api.get<{ code: number, msg: string, data?: Project[] }>(`/projects/area/${protocolId}`);
    return response;
  } catch (error) {
    console.error('Error fetching projects by protocol ID:', error);
    throw error;
  }
};

// Create a new project
export const createProject = async (projectData: Partial<Project>): Promise<{ data: ApiResponse }> => {
  try {
    const response = await api.post<ApiResponse>('/projects/create', projectData);
    return response;
  } catch (error) {
    console.error('Error creating project:', error);
    throw error;
  }
};

// Update an existing project
export const updateProject = async (id: number, projectData: Partial<Project>): Promise<{ data: ApiResponse }> => {
  try {
    const response = await api.put<ApiResponse>(`/projects/${id}`, projectData);
    return response;
  } catch (error) {
    console.error('Error updating project:', error);
    throw error;
  }
};

// Get project details by ID
export const getProjectById = async (id: number): Promise<{ data: { code: number, msg: string, data?: Project } }> => {
  try {
    const response = await api.get<{ code: number, msg: string, data?: Project }>(`/projects/${id}`);
    return response;
  } catch (error) {
    console.error('Error fetching project details:', error);
    throw error;
  }
};

// Get devices by project ID
export const getDevicesByProjectId = async (projectId: number): Promise<{ data: { code: number, msg: string, data?: any[] } }> => {
  try {
    const response = await api.get<{ code: number, msg: string, data?: any[] }>(`/devices/project/${projectId}`);
    return response;
  } catch (error) {
    console.error('Error fetching devices by project ID:', error);
    throw error;
  }
};

// Get devices with filter parameters
export const getDevicesWithFilters = async (filters: { isPay?: number, limit?: number }): Promise<{ data: { code: number, msg: string, data?: any[] } }> => {
  try {
    const response = await api.get<{ code: number, msg: string, data?: any[] }>('/devices', {
      params: filters
    });
    return response;
  } catch (error) {
    console.error('Error fetching devices with filters:', error);
    throw error;
  }
};

// Create a new device
export const createDevice = async (deviceData: any): Promise<{ data: ApiResponse }> => {
  try {
    const response = await api.post<ApiResponse>('/devices/create', deviceData);
    return response;
  } catch (error) {
    console.error('Error creating device:', error);
    throw error;
  }
};

// Update a device
export const updateDevice = async (id: number, deviceData: any): Promise<{ data: ApiResponse }> => {
  try {
    const response = await api.put<ApiResponse>(`/devices/${id}`, deviceData);
    return response;
  } catch (error) {
    console.error('Error updating device:', error);
    throw error;
  }
};

// Get device details by ID
export const getDeviceById = async (id: number): Promise<{ data: { code: number, msg: string, data?: any } }> => {
  try {
    const response = await api.get<{ code: number, msg: string, data?: any }>(`/devices/${id}`);
    return response;
  } catch (error) {
    console.error('Error fetching device details:', error);
    throw error;
  }
};

// Delete a device (soft delete)
export const deleteDevice = async (id: number): Promise<{ data: ApiResponse }> => {
  try {
    const response = await api.delete<ApiResponse>(`/devices/${id}`);
    return response;
  } catch (error) {
    console.error('Error deleting device:', error);
    throw error;
  }
};

// Get device live data
export const getDeviceLiveData = async (region: string, deviceCode: string): Promise<{ data: ApiResponse }> => {
  try {
    const response = await api.get<ApiResponse>(`/devices/live-data`, {
      params: {
        region,
        device_code: deviceCode
      }
    });
    return response;
  } catch (error) {
    console.error('Error fetching device live data:', error);
    throw error;
  }
};

// Form management interfaces
export interface FormConfigResponse {
  code: number;
  msg: string;
  data?: {
    items: FormConfig[];
    total: number;
  };
}

// Get form configurations
export const getFormConfigs = async (
  type?: number,
  page: number = 1,
  limit: number = 100
): Promise<{ data: FormConfigResponse }> => {
  try {
    // 构建查询参数
    const params = new URLSearchParams();
    if (type !== undefined) {
      params.append('type', type.toString());
    }
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    const response = await api.get<FormConfigResponse>(
      `/form-management?${params.toString()}`
    );
    return response;
  } catch (error) {
    console.error('Error fetching form configurations:', error);
    throw error;
  }
};

// Get form configuration by ID
export const getFormConfigById = async (
  formId: string
): Promise<{ data: ApiResponse<FormConfig> }> => {
  try {
    const response = await api.get<ApiResponse<FormConfig>>(`/form-management/${formId}`);
    return response;
  } catch (error) {
    console.error('Error fetching form configuration by ID:', error);
    throw error;
  }
};

// Create or update form configuration
export const saveFormConfig = async (
  formConfig: FormConfig
): Promise<{ data: ApiResponse }> => {
  try {
    const response = await api.post<ApiResponse>('/form-management', formConfig);
    return response;
  } catch (error) {
    console.error('Error saving form configuration:', error);
    throw error;
  }
};

// Delete form configuration
export const deleteFormConfig = async (
  formId: string
): Promise<{ data: ApiResponse }> => {
  try {
    const response = await api.delete<ApiResponse>(`/form-management/${formId}`);
    return response;
  } catch (error) {
    console.error('Error deleting form configuration:', error);
    throw error;
  }
};

// Ezviz Cloud Video Token interfaces
export interface EzvizVideoToken {
  uid: string;
  appKey: string;
  appSecret: string;
  token: string;
  tokenExpire: number;
  remark: string;
}

export interface EzvizVideoTokensResponse {
  code: number;
  msg: string;
  data?: {
    items: EzvizVideoToken[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }
  };
}

// Get Ezviz video tokens with pagination
export const getEzvizVideoTokens = async (
  page: number = 1,
  limit: number = 10,
  search?: string
): Promise<{ data: EzvizVideoTokensResponse }> => {
  try {
    const response = await api.get(`/ys/video-tokens`, {
      params: { page, limit, search }
    });
    return { data: response.data as EzvizVideoTokensResponse };
  } catch (error) {
    console.error('Error fetching Ezviz video tokens:', error);
    throw error;
  }
};

// Create a new Ezviz video token
export const createEzvizVideoToken = async (
  tokenData: Omit<EzvizVideoToken, 'uid' | 'token' | 'tokenExpire'>
): Promise<{ data: ApiResponse }> => {
  try {
    const response = await api.post('/ys/video-tokens', tokenData);
    return { data: response.data as ApiResponse };
  } catch (error) {
    console.error('Error creating Ezviz video token:', error);
    throw error;
  }
};

// Delete an Ezviz video token
export const deleteEzvizVideoToken = async (
  uid: string
): Promise<{ data: ApiResponse }> => {
  try {
    const response = await api.delete(`/ys/video-tokens/${uid}`);
    return { data: response.data as ApiResponse };
  } catch (error) {
    console.error('Error deleting Ezviz video token:', error);
    throw error;
  }
};

// Update an Ezviz video token
export const updateEzvizVideoToken = async (
  uid: string,
  tokenData: Pick<EzvizVideoToken, 'appKey' | 'appSecret' | 'remark'>
): Promise<{ data: ApiResponse }> => {
  try {
    const response = await api.put(`/ys/video-tokens/${uid}`, tokenData);
    return { data: response.data as ApiResponse };
  } catch (error) {
    console.error('Error updating Ezviz video token:', error);
    throw error;
  }
};

// Get Yingshi Cloud video token by UID
export const getYingshiVideoTokenByUid = async (uid: string): Promise<{ data: ApiResponse<any> }> => {
  try {
    const response = await api.get<ApiResponse<any>>(`/ys/video-tokens/${uid}`);
    return response;
  } catch (error) {
    console.error('Error fetching Yingshi video token by UID:', error);
    throw error;
  }
};

// Get all Yingshi Cloud devices by appKey
export const getYingshiDevicesByAppKey = async (appKey: string): Promise<{ data: ApiResponse<any[]> }> => {
  try {
    const response = await api.get<ApiResponse<any[]>>(`/ys/devices/all`, {
      params: { appKey }
    });
    return response;
  } catch (error) {
    console.error('Error fetching Yingshi devices by appKey:', error);
    throw error;
  }
};

// Get Yingshi Cloud cameras by deviceSerial and appKey
export const getYingshiCamerasByDeviceSerial = async (deviceSerial: string, appKey: string): Promise<{ data: ApiResponse<any[]> }> => {
  try {
    const response = await api.get<ApiResponse<any[]>>(`/ys/devices/${deviceSerial}/cameras`, {
      params: { appKey }
    });
    return response;
  } catch (error) {
    console.error('Error fetching Yingshi cameras by deviceSerial:', error);
    throw error;
  }
};

export interface DeviceAction {
  id: number;
  title: string;
  action: string;
  url: string;
  deviceType: number;
  method?: string; // 'get' or 'post'
  params?: Record<string, any>; // Optional custom parameters
}

export interface DeviceActionsResponse {
  code: number;
  msg: string;
  data?: DeviceAction[];
}

export const getDeviceActionsByArea = async (
  projectArea: number
): Promise<{ data: DeviceActionsResponse }> => {
  try {
    return await api.get<DeviceActionsResponse>(`/areas/${projectArea}/actions`);
  } catch (error) {
    console.error("Error fetching device actions:", error);
    throw error;
  }
};

// Interface for device log data
export interface DeviceLog {
  id: number;
  deviceCode: string;
  deviceName?: string;
  logTime: string;
  content: string;
  type: string;
  status: number;
  [key: string]: any; // Allow for other properties that might be returned
}

// Interface for device logs response
export interface DeviceLogsResponse {
  code: number;
  msg: string;
  data?: DeviceLog[] | {
    items: DeviceLog[];
    total: number;
  };
}

// Function to get device logs
export const getDeviceLogs = async (
  areaId: number,
  deviceCode: string
): Promise<{ data: DeviceLogsResponse }> => {
  try {
    return await api.get<DeviceLogsResponse>(`/devices/logs?areaId=${areaId}&deviceCode=${deviceCode}`);
  } catch (error) {
    console.error("Error fetching device logs:", error);
    throw error;
  }
};

export const getYingshiLiveAddress = (appKey: string, deviceSerial: string, channelNo: number = 1) => {
  return api.get<ApiResponse<any[]>>(`/ys/live-address/?appKey=${appKey}&deviceSerial=${deviceSerial}&channelNo=${channelNo}`);
};

// Delete a project
export const deleteProject = async (id: number): Promise<{ data: ApiResponse }> => {
  try {
    const response = await api.delete<ApiResponse>(`/projects/${id}`);
    return response;
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};

export default api; 